import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Globe, ArrowRight, Loader2 } from "lucide-react";
import { CitaButton } from "@/components/landing/CitaModal";
import { WHATSAPP_URL, WhatsAppIcon } from "@/components/landing/WhatsApp";
import { LEGAL_NOTE } from "@/components/landing/legalNote";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { nombre: "", comunidad: "", telefono: "", email: "", mensaje: "" };

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-base text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-500/60 focus:bg-white/10 transition-colors duration-300";

export default function Contacto() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Solicitud enviada correctamente. Le contactaremos en menos de 24 horas laborables.");
      setForm(empty);
    } catch {
      toast.error("No se pudo enviar la solicitud. Inténtelo de nuevo en unos minutos.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" data-testid="contacto-section" className="bg-slate-900 text-white py-24 sm:py-[clamp(2rem,3.5vw,3rem)] lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-16 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="min-w-0"
        >
          <div className="w-full max-w-[520px]">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-slate-500 mb-6">Atención profesional y propuestas</p>
          <h2 className="font-display section-title text-white" data-testid="contacto-title">
            Contacta con AFGestiones
          </h2>
          <p className="mt-6 mb-8 max-w-[520px] text-[clamp(1rem,1.15vw,1.125rem)] font-normal leading-[1.6] text-slate-400">
            Solicita una propuesta personalizada para tu comunidad o concierta una reunión informativa.
            Analizamos la situación técnica y económica de tu edificio con total confidencialidad.
          </p>

          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="mt-10 lg:mt-8 w-full bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 sm:p-10 lg:p-8 space-y-4 lg:space-y-3"
          >
            <div>
              <label htmlFor="nombre" className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2.5">Nombre y Apellidos</label>
              <input id="nombre" data-testid="contact-input-nombre" required value={form.nombre} onChange={set("nombre")} placeholder="Ej. María Betancor" className={inputCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <label htmlFor="telefono" className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2.5">Teléfono</label>
                <input id="telefono" type="tel" data-testid="contact-input-telefono" required value={form.telefono} onChange={set("telefono")} placeholder="Ej. 600 123 456" className={inputCls} />
              </div>
              <div>
                <label htmlFor="email" className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2.5">Correo Electrónico</label>
                <input id="email" type="email" data-testid="contact-input-email" required value={form.email} onChange={set("email")} placeholder="maria@ejemplo.com" className={inputCls} />
              </div>
            </div>
            <div>
              <label htmlFor="comunidad" className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2.5">Nombre / Dirección de la Comunidad</label>
              <input id="comunidad" data-testid="contact-input-comunidad" required value={form.comunidad} onChange={set("comunidad")} placeholder="Ej. Residencial Las Palmas II, C/ León y Castillo 100" className={inputCls} />
            </div>
            <div>
              <label htmlFor="mensaje" className="block text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2.5">Mensaje o Consulta</label>
              <textarea id="mensaje" rows={3} data-testid="contact-input-mensaje" required value={form.mensaje} onChange={set("mensaje")} placeholder="Indique número aproximado de viviendas o cualquier consulta..." className={`${inputCls} resize-none`} />
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white font-semibold py-4 rounded-lg transition-colors duration-300 shadow-lg shadow-amber-900/20"
              >
                {sending ? (
                  <>Enviando <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Solicitar Propuesta Personalizada <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <CitaButton testId="contact-cita-btn" />
            </div>
            <label className="flex items-start gap-3 text-xs text-slate-400 leading-relaxed cursor-pointer" data-testid="contact-privacidad-label">
              <input type="checkbox" required data-testid="contact-input-privacidad" className="mt-0.5 accent-amber-600" />
              <span>
                He leído y acepto la{" "}
                <Link to="/politica-de-privacidad" className="underline underline-offset-2 hover:text-white transition-colors duration-300">
                  Política de Privacidad
                </Link>
                .
              </span>
            </label>
            <p data-testid="contact-legal-note" className="text-[11px] leading-relaxed text-slate-500">{LEGAL_NOTE}</p>
          </form>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="min-w-0 w-full max-w-[520px]"
        >
          <img src="/logo-white.png" alt="AFGestiones — Gestión y Administración de Fincas" data-testid="contacto-logo" className="h-14 w-auto self-start" />
          <p className="mt-4 text-slate-400 leading-relaxed max-w-md">
            Servicios especializados de administración patrimonial y mediación comunitaria en Gran Canaria.
          </p>

          <div className="mt-10 lg:mt-8 space-y-5" data-testid="contacto-info">
            <div className="flex items-start gap-4">
              <Mail className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-slate-200">Correo Electrónico</p>
                <a href="mailto:gestiones@afgestiones.es" data-testid="contact-email-link" className="text-slate-300 hover:text-white transition-colors duration-300">
                  gestiones@afgestiones.es
                </a>
                <p className="mt-1 text-xs text-slate-500">Correo oficial para propuestas, facturas y convocatorias.</p>
              </div>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-btn"
              title="Canal de mensajería y atención de AFGestiones"
              className="flex items-start gap-4 group"
            >
              <WhatsAppIcon className="w-4 h-4 mt-1 text-[#25D366] shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-slate-200 group-hover:text-white transition-colors duration-300">Canal Oficial de Mensajería</p>
                <p className="text-slate-300">+34 674 342 645</p>
                <p className="mt-1 text-xs text-slate-500 leading-relaxed">Canal oficial de mensajería para comunicaciones administrativas. Las consultas se gestionan por orden de llegada en días hábiles durante nuestra jornada de trabajo. Para la notificación de averías en el inmueble, rogamos utilizar el formulario de Notificar Incidencia.</p>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <Phone className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-slate-200">Atención Telefónica y Citas</p>
                <a href="tel:+34674342645" data-testid="contact-phone-link" className="text-slate-300 hover:text-white transition-colors duration-300">
                  +34 674 342 645
                </a>
                <p className="mt-1 text-xs text-slate-500">Atención presencial en despacho bajo cita previa acordada.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-slate-200">Sede Central</p>
                <p className="text-slate-300">Calle Agustín Millares, 18, 35001 Las Palmas de Gran Canaria (Vegueta)</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Globe className="w-4 h-4 mt-1 text-amber-500 shrink-0" />
              <div className="text-sm">
                <p className="font-semibold text-slate-200">Cobertura Insular</p>
                <p className="text-slate-300 leading-relaxed">Gestión patrimonial con cobertura en Las Palmas de Gran Canaria, Arucas, Telde, Ingenio, Agüimes, Santa Lucía de Tirajana y San Bartolomé de Tirajana.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
